import { Button } from '@/modules/Button';
import { Dropdown } from '@/modules/Dropdown';
import { Title } from '@/modules/Title';
import { Separator } from '@/modules/Separator';
import { Input } from '@/modules/Input';
import { DatePicker } from '@/modules/DatePicker';
import { Textarea } from '@/modules/Textarea';
import { Popup } from '@/modules/Popup';
import { useFilters } from '../../hooks/useFilters';
import { FiltersProps, FilterValue } from '@/shared/types/filters';
import { useRef } from 'react';
import { PopupRef } from '@/shared/types/popup';
import { faFilter } from '@fortawesome/pro-regular-svg-icons';

export const Filters = ({
  filters = [],
  clearText = 'Limpiar',
  title = 'Filtros',
  icon = faFilter,
  label = 'Filtros',
  onClear,
  onApply,
  size = 'xs',
  theme = 'solid',
  color = 'secondary',
  closeOnApply = true,
}: FiltersProps) => {
  const popupRef = useRef<PopupRef>(null);

  const {
    filterStates,
    handleFilterChange,
    handleClear,
    handleApply: originalHandleApply,
  } = useFilters({
    filters,
    onClear,
    onApply,
  });

  // Función modificada para cerrar el popup después de aplicar
  const handleApply = () => {
    originalHandleApply();
    if (closeOnApply) {
      popupRef.current?.close();
    }
  };

  // Función para cerrar todos los dropdowns abiertos
  const closeAllDropdowns = () => {
    // Emitir evento para cerrar todos los dropdowns
    const event = new CustomEvent('close-all-dropdowns');
    document.dispatchEvent(event);
  };

  // Función que se ejecuta cuando el popup se cierra
  const handlePopupClose = () => {
    closeAllDropdowns();
  };

  // Función que se ejecuta cuando se hace click fuera de un dropdown
  const handleDropdownOutsideClick = () => {
    // Cerrar el popup cuando se haga click fuera de cualquier dropdown
    popupRef.current?.close();
  };

  return (
    <Popup
      borderRadius="2xl"
      absolute={false}
      color={color}
      label={label}
      leftSlot={faFilter}
      size={size}
      closeOnClick={false}
      closeOnClickOutside={true}
      className="ui-w-[320px]"
      ref={popupRef}
      onClose={handlePopupClose}
    >
      <div className="ui-flex ui-items-center ui-justify-between ui-p-16 ui-pb-12">
        <Title title={title} icon={icon} />
        <Button theme="solid" color="secondary" pill="sm" size="xxs" onClick={handleClear}>
          {clearText}
        </Button>
      </div>
      <Separator />
      <div className="ui-flex ui-flex-col ui-gap-y-8 ui-p-16 ui-pt-12">
        {filters.map((filter, index) => {
          // Usar la misma lógica que useFilters para obtener la clave del estado
          const stateKey = filter.keyName || filter.label.toLowerCase();
          const uniqueKey = `filter-${index}-${filter.type}-${filter.label}`;

          if (filter.type === 'dropdown') {
            return (
              <Dropdown
                key={uniqueKey}
                field={{
                  size: 'xs',
                  label: filter.label,
                  placeholder: filter.placeholder,
                }}
                items={{ options: filter.options || [] }}
                values={{
                  value:
                    (filterStates[stateKey] as string | number | (string | number)[] | undefined) ||
                    '',
                }}
                events={{
                  onValueChange: (value) =>
                    handleFilterChange(filter.label, value as FilterValue, filter),
                }}
                popover={{ fullWidth: true, absolute: false, closeOnClickOutside: true }}
              />
            );
          } else if (filter.type === 'multiple') {
            return (
              <Dropdown
                key={uniqueKey}
                field={{ label: filter.label, placeholder: filter.placeholder, size: 'xs' }}
                items={{ options: filter.options || [] }}
                values={{
                  value:
                    (filterStates[stateKey] as string | number | (string | number)[] | undefined) ||
                    '',
                }}
                events={{
                  onValueChange: (value) =>
                    handleFilterChange(filter.label, value as FilterValue, filter),
                }}
                multi={{ multiple: true }}
                popover={{ absolute: false, closeOnClickOutside: true }}
              />
            );
          } else if (filter.type === 'group') {
            return (
              <Dropdown
                key={uniqueKey}
                field={{ label: filter.label, placeholder: filter.placeholder, size: 'xs' }}
                items={{ options: filter.options }}
                values={{
                  value:
                    (filterStates[stateKey] as string | number | (string | number)[] | undefined) ||
                    '',
                }}
                events={{
                  onValueChange: (value) =>
                    handleFilterChange(filter.label, value as FilterValue, filter),
                }}
                menuSearch={{ searchable: true }}
                popover={{ footer: true }}
                multi={{ multiple: true, selectionSummary: 'tags' }}
                checkbox={{ withCheckbox: true }}
              />
            );
          } else if (filter.type === 'input') {
            return (
              <Input
                key={uniqueKey}
                label={filter.label}
                value={typeof filterStates[stateKey] === 'string' ? filterStates[stateKey] : ''}
                onChange={(e) => handleFilterChange(filter.label, e.target.value, filter)}
                size="xs"
              />
            );
          } else if (filter.type === 'date') {
            return (
              <DatePicker
                key={uniqueKey}
                closeOnSelect
                size="xs"
                label={filter.label}
                selectedDate={
                  filterStates[stateKey] instanceof Date
                    ? filterStates[stateKey]
                    : filterStates[stateKey] === null
                      ? null
                      : null
                }
                onDateChange={(value) =>
                  handleFilterChange(filter.label, value as FilterValue, filter)
                }
                placeholder={filter.placeholder}
              />
            );
          } else if (filter.type === 'dateRange') {
            return (
              <DatePicker
                key={uniqueKey}
                size="xs"
                label={filter.label}
                mode="range"
                selectedRange={
                  filterStates[stateKey] &&
                  typeof filterStates[stateKey] === 'object' &&
                  'start' in (filterStates[stateKey] as object)
                    ? (filterStates[stateKey] as { start: Date | null; end: Date | null })
                    : { start: null, end: null }
                }
                onRangeChange={(value) =>
                  handleFilterChange(filter.label, value as FilterValue, filter)
                }
                placeholder={filter.placeholder}
              />
            );
          } else if (filter.type === 'textarea') {
            return (
              <Textarea
                key={uniqueKey}
                value={typeof filterStates[stateKey] === 'string' ? filterStates[stateKey] : ''}
                onChange={(value) => handleFilterChange(filter.label, value as FilterValue, filter)}
              />
            );
          }

          return null;
        })}

        <Button color="primary" onClick={handleApply}>
          Aplicar
        </Button>
      </div>
    </Popup>
  );
};
